<template>
  <div
    v-if="facets"
    :class="{
      'facets-menu-fi': true,
      'facets-menu-fi--has-facets': displayedFacets.length,
      'facets-menu-fi--has-chips': selectedElements.length,
      'facets-menu-fi--clip': clip,
    }"
  >
    <div
      v-if="isSM || showFacetsMobile || notMounted"
      :class="{
        'facets-menu-fi__facets': true,
        'facets-menu-fi__facets--leaving': leavingFacetsMobile,
        'facets-menu-fi__facets--show-result-btn': showMobileResultButton,
        'facets-menu-fi__facets--ssr': notMounted,
        [`facets-menu-fi__facets--cols-${columnsMax}`]: columnsMax,
      }"
    >
      <div
        v-if="!isSM || notMounted"
        class="facets-menu-fi__mobile-filter-header"
      >
        <div
          class="facets-menu-fi__mobile-filter-back"
          data-test="close-mobile-facets"
          @click="closeMobileFacets"
        >
          <div class="facets-menu-fi__mobile-filter-toggle">
            <icon-fi icon="arrow-left" />
            {{ $dict.get('facets.Show-Filters-Mobile') }}
          </div>
        </div>
        <clear-all-facets-fi
          v-if="allowClearAll && selectedElements.length"
          data-test="clear-all-facets"
          @click="clearAllFacets"
        />
      </div>

      <div
        :style="isSM ? facetsStyle : {}"
        :class="{
          'facets-menu-fi__facets-wrapper': true,
          'facets-menu-fi__facets-wrapper--transitioning': isClosing || isOpening,
        }"
      >
        <div
          ref="selectedFacets"
          :class="{
            'facets-menu-fi__selected-facets': true,
            'facets-menu-fi__selected-facets--fite': isFite,
            'facets-menu-fi__selected-facets--active': showFilters,
            'facets-menu-fi__selected-facets--centered': centered,
            'facets-menu-fi__selected-facets--ssr': notMounted,
          }"
        >
          <div
            v-for="(facet, idx) in displayedFacets"
            :key="facet.name"
            :class="[
              'facets-menu-fi__selected-facet',
              idx === 0 && centered && displayedFacets.length < maxFacetsInRow
                ? `facets-menu-fi__selected-facet--start-${centeredSpace}`
                : null,
            ]"
          >
            <slot
              name="facets"
              :facet="facet"
              :open="openFacet === facet.name"
              :toggle-open="toggleOpen"
              :change="change"
              :close="close"
              :idx="idx"
              :register-dom-element="registerDomElement"
            >
              <select-fi
                ref="facetsComponent"
                :elements="facet.elements"
                :name="facet.name"
                :multiselect="facet.multiselect"
                :open="openFacet === facet.name"
                large
                @change="change"
                @toggle-open="toggleOpen(facet)"
                @close="close(facet)"
              />
            </slot>
          </div>
        </div>
      </div>

      <button-fi
        v-if="showMobileResultButton && !notMounted"
        type="button"
        class="facets-menu-fi__show-results-mobile button-fi__primary--large"
        data-test="show-results"
        @click="closeMobileFacets"
      >
        {{ $dict.get('search.Show-Results') }}
      </button-fi>
    </div>
    <div
      v-if="(!isSM || notMounted) && displayedFacets?.length"
      class="facets-menu-fi__mobile-filter-toggle-wrapper"
    >
      <div
        class="facets-menu-fi__mobile-filter-toggle"
        :class="{ 'facets-menu-fi__mobile-filter-fixed': filtersOutOfView && !isFite }"
        data-test="open-mobile-facets"
        @click="openMobileFacets"
      >
        {{ $dict.get('facets.Show-Filters-Mobile') }}
        <icon-fi icon="filter" />
      </div>
    </div>

    <div
      v-if="(isSM || notMounted) && nonHiddenFacets.length > compactedFacets.length"
      class="facets-menu-fi__filters"
    >
      <div
        class="facets-menu-fi__filters-headline"
        data-test="toggle-facets"
        @click="toggleFacets"
      >
        {{
          showFilters ? $dict.get('facets.Hide-All-Filters') : $dict.get('facets.Show-All-Filters')
        }}
        <span
          :class="{
            'facets-menu-fi__chevron': true,
            'facets-menu-fi__chevron--active': showFilters && !isClosing,
          }"
        >
          <icon-fi icon="chevron-down" />
        </span>
      </div>
    </div>

    <div
      v-if="selectedElements.length"
      class="facets-menu-fi__bottom"
    >
      <div class="facets-menu-fi__selected-facets facets-menu-fi__chips">
        <facets-selection-fi
          v-for="selection in selectedElements"
          :key="`${selection.facet.name} ${selection.element.name}`"
          :element="selection.element"
          :facet="selection.facet"
          @change="change"
        >
          <template
            v-if="$slots.selection"
            #display="{ facet, element }"
          >
            <slot
              name="selection"
              :facet="facet"
              :element="element"
            />
          </template>
        </facets-selection-fi>
      </div>

      <div
        v-if="allowClearAll && selectedElements.length && isSM && showClearAll"
        class="facets-menu-fi__clear-all-btn-wrapper"
      >
        <clear-all-facets-fi
          data-test="clear-all-facets"
          @click="clearAllFacets"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ButtonFi, IconFi, SelectFi } from 'atoms';
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';

import ClearAllFacetsFi from '@/components/facets/clear-all-facets-fi/ClearAllFacetsFi.vue';
import type { GenericFacet, GenericFacetElement } from '@/components/facets/facet-types';
import FacetsSelectionFi from '@/components/facets/facets-selection-fi/FacetsSelectionFi.vue';
import { useBodyScroll } from '@/composables/bodyScroll';
import { useBreakpoints } from '@/composables/breakpoint';

defineOptions({ name: 'FacetsMenuFi' });

interface FacetSelection {
  facet: any;

  element: any;
}

interface Props {
  facets?: any[];
  allowClearAll?: boolean;
  centered?: boolean;
  showClearAll?: boolean;
  columnsMax?: number;
  filtersOutOfView?: boolean;
  clip?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  facets: undefined,
  allowClearAll: true,
  centered: false,
  showClearAll: true,
  columnsMax: 6,
  filtersOutOfView: false,
  clip: false,
});

const emit = defineEmits<{
  change: [...payload: any[]];
  'clear-all-facets': [];
  'mobile-facet-closed': [];
}>();

const { isSM, isLG, isXL, windowDimensions } = useBreakpoints();
const { bodyDisableScroll, bodyEnableScroll } = useBodyScroll();

const selectedFacetsRef = useTemplateRef('selectedFacets');
const facetsRef = useTemplateRef<ComponentPublicInstance[]>('facetsComponent'); // NOTE: unfortunately the template compiler will not correctly pick up the v-for here -> manually typed

const notMounted = ref(true);
const showFacetsMobile = ref(false);
const leavingFacetsMobile = ref(false);
const openFacet = ref<string | null>(null);
const showFilters = ref(false);
const isClosing = ref(false);
const isOpening = ref(false);
const facetsStyle = ref<Record<string, string | undefined>>({});
let collapseToFacet: Element | null = null;

const maxFacetsInRow = computed(() => {
  if (isXL.value) {
    return props.columnsMax;
  }
  if (isLG.value) {
    return 4;
  }
  return 6;
});

const nonHiddenFacets = computed(() => (props.facets ?? []).filter((f) => !f.noMenu));

const compactedFacets = computed(() => nonHiddenFacets.value.slice(0, maxFacetsInRow.value));

const displayedFacets = computed(() =>
  showFilters.value || !isSM.value ? nonHiddenFacets.value : compactedFacets.value,
);

const selectedElements = computed(() => {
  const result: FacetSelection[] = [];
  (props.facets ?? []).forEach((facet) => {
    (facet.elements ?? []).forEach((element: GenericFacetElement) => {
      if (element.selected) {
        result.push({ facet, element });
      }
    });
  });
  return result;
});

const showMobileResultButton = computed(() => !isSM.value && selectedElements.value.length > 0);

const centeredSpace = computed(() => {
  const gridCols = 12;
  const itemsCols = (gridCols / props.columnsMax) * displayedFacets.value.length;
  return (gridCols - itemsCols) / 2 + 1;
});

watch(
  () => props.facets?.length,
  async () => {
    if (showFilters.value) {
      await nextTick();
      setFacetsMaxHeight();
    }
  },
);

watch(
  () => windowDimensions.value,
  () => {
    resize();
  },
  { deep: true },
);

onMounted(() => {
  notMounted.value = false;
});

const registerDomElement = (element: Element) => {
  collapseToFacet = element;
};

const clearAllFacets = () => {
  emit('clear-all-facets');
};

const openMobileFacets = () => {
  bodyDisableScroll();
  showFacetsMobile.value = true;
};

const closeMobileFacets = async () => {
  leavingFacetsMobile.value = true;
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 300);
  });
  bodyEnableScroll();
  showFacetsMobile.value = false;
  leavingFacetsMobile.value = false;
  emit('mobile-facet-closed');
};

const setFacetsMaxHeight = () => {
  if (notMounted.value) {
    return;
  }
  if (!selectedFacetsRef.value) {
    return;
  }
  const { height } = selectedFacetsRef.value.getBoundingClientRect();
  facetsStyle.value = {
    'max-height': `${height}px`,
  };
};

const getFirstRowHeightStyle = () => {
  const element = collapseToFacet || selectedFacetsRef.value?.querySelector('.select-fi');
  if (!element) {
    return {};
  }
  const { height: elementHeight } = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  const height = elementHeight + Number.parseFloat(style.marginBottom);
  return {
    'max-height': `${height}px`,
  };
};

const toggleFacets = async () => {
  if ((!facetsRef.value || facetsRef.value.length === 0) && !collapseToFacet) {
    showFilters.value = !showFilters.value;
    return;
  }

  if (showFilters.value) {
    setFacetsMaxHeight();
    isClosing.value = true;
    await nextTick();

    facetsStyle.value = getFirstRowHeightStyle();
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 300);
    });

    showFilters.value = false;
    isClosing.value = false;
    facetsStyle.value = {};
  } else {
    facetsStyle.value = getFirstRowHeightStyle();
    showFilters.value = true;
    isOpening.value = true;
    if (selectedFacetsRef.value) {
      await nextTick();
      setFacetsMaxHeight();
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 300);
      });
    }
    isOpening.value = false;
  }
};

const toggleOpen = (facet: GenericFacet) => {
  if (openFacet.value === facet.name) {
    openFacet.value = null;
  } else {
    openFacet.value = facet.name;
  }
};

const close = (facet: GenericFacet) => {
  if (openFacet.value === facet.name) {
    openFacet.value = null;
  }
};

const change = (...payload: any[]) => {
  emit('change', ...payload);
};

const resize = () => {
  if (isSM.value) {
    bodyEnableScroll();
  }
};

defineExpose({ openMobileFacets });
</script>

<style scoped src="./facets-menu-fi" />

<style src="./facets-menu-fi-unscoped" />
